import axios from "axios";
import { invalidateOabEligibility } from "app/services/oabEligibility/invalidateOabEligibility";
import { trackOabConfirmed } from "./trackOabConfirmedEvent";
import {
  amountOpenDeadlines,
  checkOpenDeadlines,
  createDeadline,
  deleteDeadline,
  getDeadlineCnjInfo,
  getDeadlines,
  getDeadlinesByCnjMonitoring,
  updateDeadline,
} from "../../Deadlines/api/deadlineV2";

export {
  amountOpenDeadlines,
  checkOpenDeadlines,
  createDeadline,
  deleteDeadline,
  getDeadlines,
  getDeadlinesByCnjMonitoring,
  updateDeadline,
};

const baseAPI = process.env.REACT_APP_API_URL;

export const getLawsuitsMonitorInfo = (complete = false) => {
  return axios
    .get(
      `${baseAPI}/monitoring/info?${new URLSearchParams({
        complete,
      }).toString()}`
    )
    .then(response => response.data);
};

export const getDeadlineCountInfo = (complete = false) => {
  return amountOpenDeadlines(complete);
};

export const getLawsuits = (params, hasFilters) => {
  return hasFilters ? getLawsuitsWithFilters(params) : getLawsuitsWithoutFilters(params)
}

export const getLawsuitsWithFilters = (params = {}, { signal } = {}) =>{
  return axios
    .post(`${baseAPI}/monitoring/v4/lawsuit`, { ...params }, { signal })
    .then((r) => r.data)
};


export const getLawsuitsWithoutFilters = (params = {}) => {
    return axios
      .get(`${baseAPI}/monitoring/v4/lawsuit?${new URLSearchParams(params)}`)
      .then(response => response.data);
};

export const getLawsuitDetails = async (cnjWatcherId, { withDeadlineCount = true } = {}) => {
  const [deadlineCountResult, { data: lawSuitDetails }] = await Promise.all([
    withDeadlineCount ? getDeadlineCnjInfo(cnjWatcherId) : Promise.resolve(null),
    axios.get(`${baseAPI}/monitoring/lawsuit-details/${cnjWatcherId}`),
  ]);

  return {
    ...lawSuitDetails,
    ...(deadlineCountResult ? { stepDeadlineCount: deadlineCountResult.count } : {}),
  };
};

/*
  Vínculo processo↔cliente (`manage_cases`). Contrato do PR jusfy-backend#3314
  (GDC-1695) — AINDA NÃO DEPLOYADO em produção quando este código foi escrito
  (2026-09-14); ver a dependência na feature spec
  `docs/specs/aba-processos-cliente`. Substitui o contrato anterior do PR
  #3274: agora um processo pode ter N clientes vinculados ao mesmo caso
  (litisconsórcio), e existe uma listagem paginada por cliente (abaixo) — o
  antigo `getManageCaseLawsuitIdsByCustomers` (lote de só ids, sem paginação)
  saiu por não ter mais consumidor.
*/

/**
 * Processos vinculados a UM cliente, paginado — já no shape completo de
 * `v4/lawsuit` (nome, tribunal, status etc.), não só ids.
 */
export const getLawsuitsByCustomer = (customerId, { page = 1, pageSize = 20 } = {}) => {
  return axios
    .get(`${baseAPI}/monitoring/manage-cases/customer/${customerId}`, {
      params: { page, pageSize },
    })
    .then((response) => response.data);
};

/**
 * Desvincula UM cliente de um processo. `customerId` é obrigatório desde o
 * GDC-1695 — antes desvinculava o processo inteiro, sem parâmetro nenhum;
 * agora um caso pode ter vários clientes, então é preciso dizer qual sai. Não
 * apaga o `manage_case` nem os demais litisconsortes.
 */
export const unlinkLawsuitFromCustomer = (cnjMonitoringId, customerId) => {
  return axios.delete(`${baseAPI}/monitoring/lawsuit/${cnjMonitoringId}/manage-case`, {
    params: { customerId },
  });
};

export const getStepsAndPublications = (
  cnjMonitoringId,
  page,
  pageSize,
  listType
) => {
  return axios
    .get(
      `${baseAPI}/monitoring/lawsuit/${cnjMonitoringId}?page=${page}&page_size=${pageSize}&listType=${listType}`
    )
    .then(response => response.data);
};

export const getPublications = (
  cnjMonitoringId,
  page,
  pageSize,
  onlyStarred = false
) => {
  return axios
    .get(
      `${baseAPI}/publications/${cnjMonitoringId}?page=${page}&page_size=${pageSize}&starred=${onlyStarred}`
    )
    .then(response => response.data);
};

export const updateSteps = (cnjMonitoringId, payload) => {
  return axios
    .put(`${baseAPI}/monitoring/lawsuit/${cnjMonitoringId}/steps`, payload)
    .then(response => response.data);
};

export const updatePublication = (publicationId, payload) => {
  return axios
    .put(`${baseAPI}/publications/favorite/${publicationId}`, payload)
    .then(response => response.data);
};

export const markPublicationsAsRead = publicationIds => {
  return axios
    .put(`${baseAPI}/publications/read`, { ids: publicationIds })
    .then(response => response.data);
};

export const updateStepById = (cnjMonitoringId, stepId, payload) => {
  return axios
    .put(
      `${baseAPI}/monitoring/lawsuit/${cnjMonitoringId}/step/${stepId}`,
      payload
    )
    .then(response => response.data);
};

export const setupOAB = async (oabNumber, uf, surface) => {
  return await axios
    .post(`${baseAPI}/monitoring/v2/oab/create`, { oabNumber, uf })
    .then(response => {
      invalidateOabEligibility();
      trackOabConfirmed("manual", surface);
      return response.data;
    });
};

export const findOABInfo = async (oab) => {
  return axios
    .get(`${baseAPI}/monitoring/setup/info?oab=${oab}`)
    .then(response => response.data);
};

export const downloadAttachment = async (attachmentId, extension) => {
  try {
    const response = await axios({
      url: `${baseAPI}/monitoring/lawsuit/attachment/${attachmentId}/download`,
      method: "GET",
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Auto ${attachmentId}.${extension}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);

    return response;
  } catch (error) {
    throw error;
  }
};

export const getSurveyInfo = async surveyId => {
  return await axios
    .get(`${baseAPI}/user_survey/info/${surveyId}`)
    .then(response => response.data);
};

export const createSurveyInfo = async surveyId => {
  return await axios
    .post(`${baseAPI}/user_survey/info/`, { surveyId })
    .then(response => response.data);
};

export const getMonitoringPlanId = async (planType, periodicity, amount) => {
  return await axios
    .get(
      `${baseAPI}/monitoring/plans?planType=${planType}&periodicity=${periodicity}&amount=${amount}`
    )
    .then(response => response.data);
};

export const deleteOabMonitoring = async oabMonitoringId => {
  return await axios
    .delete(`${baseAPI}/monitoring/?oabMonitoringId=${oabMonitoringId}`)
    .then(response => response.data);
};

export const getScheduledMigrationPlanConfirmation = async gateway_id => {
  return await axios
    .get(
      `${baseAPI}/subscription/check-subscription-migration-pending?gateway_id=${gateway_id}`
    )
    .then(response => response.data);
};

export const createCustomJusProcessosPlan = async (
  client_id,
  jusProcessosName
) => {
  try {
    const response = await axios.post(
      `${baseAPI}/monitoring/plans/on-the-fly`,
      {
        client_id,
        jusProcessosName,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      const statusCode = error.response.status;
      console.error(`Error ${statusCode}: ${error.response.data}`);
      throw new Error(`${statusCode}: Não foi possível criar um novo plano`);
    } else if (error.request) {
      console.error("No response from the server");
      throw new Error(`Erro: Sem resposta do servidor`);
    } else {
      console.error("Error from the server:", error.message);
      throw new Error(`Erro: Não foi possível criar um novo plano`);
    }
  }
};

export const createCustomJusprocessosPlanV2 = async (
  client_id,
  units
) => {
  try {
    const response = await axios.post(
      `${baseAPI}/monitoring/plans/on-the-fly/v2`,
      {
        client_id,
        units,
      }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      const statusCode = error.response.status;
      console.error(`Error ${statusCode}: ${error.response.data}`);
      throw new Error(`${statusCode}: Não foi possível criar um novo plano`);
    } else if (error.request) {
      console.error("No response from the server");
      throw new Error(`Erro: Sem resposta do servidor`);
    } else {
      console.error("Error from the server:", error.message);
      throw new Error(`Erro: Não foi possível criar um novo plano`);
    }
  }
};

export const createManualCnjs = payload => {
  return axios
    .post(`${baseAPI}/monitoring/create/monitoring`, payload)
    .then(response => response.data);
};

export const bulkDeleteCnjs = data => {
  return axios
    .delete(`${baseAPI}/monitoring/bulk-pause-cnj`, { data })
    .then(response => response.data);
};

export const bulkDeleteMonitoring = data => {
  return axios
    .delete(`${baseAPI}/monitoring/v2`, { data })
    .then(response => response.data);
}

export const deleteDeadlineSuggestion = async id => {
  return await axios
    .delete(`${baseAPI}/deadline/suggestion/${id}`)
    .then(response => response.data);
};

export const getDeadlinesStatus = () => {
  return axios
    .get(`${baseAPI}/monitoring/status/deadlines`)
    .then(response => response.data);
};

export const getSyncStatusCount = ({ search = "", oab = "" }) => {
  return axios
    .get(`${baseAPI}/monitoring/sync_status/count?cnj=${search}&oabNumber=${oab}`)
    .then(response => response.data);
};

export const getSyncStatus = ({ search = "", oab = "", page= 1 }) => {
  return axios
    .get(`${baseAPI}/monitoring/sync_status?cnj=${search}&oabNumber=${oab}&page=${page}&pagesize=5`)
    .then(response => response.data);
};

export const clearAllFinishedSyncStatus = ({ oab = "" }) => {
  return axios
    .delete(`${baseAPI}/monitoring/sync_status/cleanup?oabNumber=${oab}`)
    .then(response => response.data);
};

export const discardSyncStatus = (id ) => {
  return axios
    .put(`${baseAPI}/monitoring/sync_status/${id}/discard`)
    .then(response => response.data);
};

export const hasExternalMonitoring = ({ nup }) => {
  return axios
    .get(`${baseAPI}/monitoring/has_external_monitoring/${nup}`)
    .then(response => response.data);
};

export const createExternalMonitoring = ({ nup }) => {
  return axios
    .post(`${baseAPI}/monitoring/external/create`, { nup, withDocuments: false, withPublications: true })
    .then(response => response.data);
};

export const getOabLoading = () => {
  return axios
    .get(`${baseAPI}/monitoring/oab/loading`)
    .then(response => response.data);
};
